import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  CreditCard,
  Check,
  Sparkles,
  Zap,
  Building,
  Crown,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    description: 'Perfect for getting started',
    features: [
      '2 domains',
      'Basic AI analysis',
      '1 site template',
      'Email lead capture',
      'Basic analytics',
    ],
    limitations: ['No YOLO mode', 'No custom CSS', 'No priority support'],
    icon: Sparkles,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    description: 'For serious domain investors',
    features: [
      '10 domains',
      'Advanced AI analysis',
      'All site templates',
      'YOLO batch mode',
      'Full analytics dashboard',
      'Custom CSS & branding',
      'Priority support',
    ],
    limitations: [],
    icon: Zap,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    popular: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    price: 99,
    description: 'For agencies & high-volume users',
    features: [
      'Unlimited domains',
      'Everything in Pro',
      'White-label options',
      'API access',
      'Team members',
      'Dedicated support',
      'Custom integrations',
    ],
    limitations: [],
    icon: Building,
    color: 'text-accent',
    bgColor: 'bg-accent/10',
    popular: false,
  },
]

export function BillingPage() {
  const { user } = useAuthStore()
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)

  const currentTier = user?.subscriptionTier || 'free'

  const checkoutMutation = useMutation({
    mutationFn: (tier: string) => api.createCheckout(tier),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const portalMutation = useMutation({
    mutationFn: () => api.createPortal(),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleUpgrade = (planId: string) => {
    if (planId === 'free') return
    setSelectedPlan(planId)
    checkoutMutation.mutate(planId)
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/settings" className="btn-ghost p-2">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-display font-bold">Billing</h1>
          <p className="text-muted-foreground">
            Manage your subscription and payment methods
          </p>
        </div>
      </div>

      {/* Current Plan */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <Crown className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="text-xl font-display font-bold capitalize">
                {currentTier}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              {user?.domainLimit || 2} domains available
            </p>
            {currentTier !== 'free' && (
              <button
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
                className="btn-outline"
              >
                {portalMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    Manage
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Choose a Plan</h2>
        <div className="grid gap-6 lg:grid-cols-3">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
              className={cn(
                'relative card border-2 transition-all',
                plan.popular
                  ? 'border-primary shadow-lg shadow-primary/10'
                  : currentTier === plan.id
                  ? 'border-success'
                  : 'border-border hover:border-primary/50'
              )}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="badge badge-primary">Most Popular</span>
                </div>
              )}

              {currentTier === plan.id && (
                <div className="absolute -top-3 right-4">
                  <span className="badge badge-success">Current</span>
                </div>
              )}

              <div className="text-center mb-6">
                <div className={cn('inline-flex p-3 rounded-xl mb-4', plan.bgColor)}>
                  <plan.icon className={cn('h-6 w-6', plan.color)} />
                </div>
                <h3 className="text-xl font-display font-bold">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-display font-bold">
                    ${plan.price}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                {plan.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-success shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
                {plan.limitations.map((limitation) => (
                  <div
                    key={limitation}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <span className="h-4 w-4 text-center shrink-0">—</span>
                    <span className="text-sm">{limitation}</span>
                  </div>
                ))}
              </div>

              {currentTier === plan.id ? (
                <button disabled className="w-full btn-outline cursor-not-allowed">
                  <Check className="h-4 w-4" />
                  Current Plan
                </button>
              ) : plan.id === 'free' ? (
                <button disabled className="w-full btn-ghost cursor-not-allowed">
                  Downgrade not available
                </button>
              ) : (
                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={
                    checkoutMutation.isPending && selectedPlan === plan.id
                  }
                  className={cn(
                    'w-full',
                    plan.popular ? 'btn-primary' : 'btn-outline'
                  )}
                >
                  {checkoutMutation.isPending && selectedPlan === plan.id ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Redirecting...
                    </>
                  ) : currentTier !== 'free' &&
                    plans.findIndex((p) => p.id === plan.id) <
                      plans.findIndex((p) => p.id === currentTier) ? (
                    'Downgrade'
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      Upgrade
                    </>
                  )}
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <details className="group">
            <summary className="cursor-pointer font-medium hover:text-primary">
              Can I change plans anytime?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              Yes! You can upgrade or downgrade your plan at any time. When you
              upgrade, you'll be charged the prorated amount. When you downgrade,
              you'll receive credit towards future invoices.
            </p>
          </details>
          <details className="group">
            <summary className="cursor-pointer font-medium hover:text-primary">
              What payment methods do you accept?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              We accept all major credit cards through Stripe, including Visa,
              Mastercard, American Express, and Discover.
            </p>
          </details>
          <details className="group">
            <summary className="cursor-pointer font-medium hover:text-primary">
              Is there a free trial?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              Yes! The Free plan lets you try out the platform with up to 2
              domains. No credit card required.
            </p>
          </details>
          <details className="group">
            <summary className="cursor-pointer font-medium hover:text-primary">
              What happens to my sites if I downgrade?
            </summary>
            <p className="mt-2 text-sm text-muted-foreground">
              Your existing sites will continue to work, but you won't be able to
              create new sites if you're over your plan's domain limit. You'll need
              to delete some sites before creating new ones.
            </p>
          </details>
        </div>
      </div>

      {/* Contact */}
      <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/50">
        <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Need help with billing?</p>
          <p className="text-sm text-muted-foreground">
            Contact us at{' '}
            <a
              href="mailto:support@unparkmydomain.com"
              className="text-primary hover:underline"
            >
              support@unparkmydomain.com
            </a>{' '}
            and we'll be happy to assist you.
          </p>
        </div>
      </div>
    </div>
  )
}
